import React, { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Ticket } from '../types';
import { Button, Flex, Input, Text, Card } from './base-components';
import { useTranslation } from '../i18n/TranslationContext';
import { trackEvent } from '../services/analytics';
import { accountLabel, ticketShortId, ticketTypeLabel } from './transferUtils';
import { AxsTransferOtpPrompt } from './AxsTransferOtpPrompt';
import { ManualTransferOtpError, ManualTransferResult } from '../api/client';

interface TransferFormData {
  email: string;
  phone: string;
  firstName: string;
  lastName: string;
}

interface Props {
  ticket: Ticket | null;
  /**
   * Extra tickets to move in the SAME transfer, alongside `ticket`.
   *
   * The manual-transfer endpoint takes a list of ticket_ids, so an event's worth
   * of AXS tickets is one call to one recipient — which is how the event view
   * offers a transfer for platforms the batch endpoint can't address. Every
   * entry must share `ticket`'s platform and event; the backend resolves the
   * external event id from the first id it is given.
   *
   * Omit it and this modal behaves exactly as it always has: one ticket.
   */
  alsoTransfer?: Ticket[];
  open: boolean;
  onClose: () => void;
  /**
   * Resolves with what the endpoint answered. AXS answers the first call with
   * an OTP challenge instead of a transfer, so this cannot be Promise<void>:
   * the modal needs to know whether to close or show the code prompt.
   */
  onTransfer: (params: TransferParams) => Promise<ManualTransferResult>;
}

export interface TransferParams {
  platform: string;
  eventId: string;
  ticketIds: string[];
  recipientEmail?: string;
  recipientPhone?: string;
  recipientFirstName?: string;
  recipientLastName?: string;
  otpSession?: string;
  otpCode?: string;
}

export const TransferTicketModal: React.FC<Props> = ({
  ticket,
  alsoTransfer,
  open,
  onClose,
  onTransfer,
}) => {
  const { t } = useTranslation();
  const [transferType, setTransferType] = useState<'email' | 'phone'>('email');

  const platform = ticket
    ? (ticket.external_platform_connection?.platform || ticket.platform || '').toUpperCase()
    : '';
  const isDice = platform === 'DICE';
  const isAxs = platform === 'AXS';
  // TAO transfers are addressed by phone number only — the platform has no
  // email-based transfer.
  const isTao = platform === 'TAO';
  // TAO is always phone; DICE offers the choice; everything else is email.
  const usePhone = isTao || (isDice && transferType === 'phone');

  const form = useForm<TransferFormData>({
    defaultValues: { email: '', phone: '', firstName: '', lastName: '' },
  });

  // Reset form when ticket changes or modal closes
  useEffect(() => {
    if (!open) {
      form.reset();
      setTransferType('email');
      setOtp(null);
      setOtpSubmitting(false);
      setOtpResending(false);
    }
  }, [open]);

  useEffect(() => {
    form.clearErrors();
  }, [transferType]);

  // Every ticket this modal could move. Deduped: `ticket` is the display ticket
  // and usually appears in `alsoTransfer` as well, and sending an id twice would
  // have the platform transfer it twice.
  const groupTickets = useMemo(() => {
    const seen = new Set<string>();
    const out: Ticket[] = [];
    [ticket, ...(alsoTransfer ?? [])].forEach((t) => {
      if (!t || !t.external_ticket_id || seen.has(t.external_ticket_id)) return;
      seen.add(t.external_ticket_id);
      out.push(t);
    });
    return out;
  }, [ticket, alsoTransfer]);

  const groupIds = useMemo(() => groupTickets.map((t) => t.external_ticket_id), [groupTickets]);
  const isGroup = groupTickets.length > 1;

  /**
   * What starts checked, as a stable string — `alsoTransfer` is sliced fresh on
   * every render by the caller, so depending on the array itself would reset
   * the selection each render and nothing could ever be unchecked.
   *
   * Everything when the group is one account: the event view's button said
   * "Transfer (6)", so all six start checked and unwanted ones get removed.
   * Nothing when it spans several, because a transfer can only carry one
   * account and picking which is the seller's call, not ours.
   */
  const defaultSelectionKey = useMemo(() => {
    if (new Set(groupTickets.map(accountLabel)).size > 1) return '';
    return groupTickets.map((t) => t.external_ticket_id).join(',');
  }, [groupTickets]);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  /**
   * The AXS code step, or null when there isn't one.
   *
   * `params` is the body of call 1, kept verbatim: call 2 is that same request
   * plus the session and the code, and a resend is that same request again. Its
   * absence is also what says "nothing has transferred yet" — a 202 is not a
   * success, so the modal stays open and the tickets stay unsent.
   */
  const [otp, setOtp] = useState<{
    params: TransferParams;
    session: string;
    phoneNumber?: string;
    /** The session behind this prompt has been burnt; only a new code moves on. */
    spent: boolean;
    error?: string;
  } | null>(null);
  const [otpSubmitting, setOtpSubmitting] = useState(false);
  const [otpResending, setOtpResending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelectedIds(new Set(defaultSelectionKey ? defaultSelectionKey.split(',') : []));
  }, [open, defaultSelectionKey]);

  const toggleTicket = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allSelected = groupIds.length > 0 && groupIds.every((id) => selectedIds.has(id));

  const toggleAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set(groupIds));
  };

  if (!open || !ticket) return null;

  const totalTickets = groupTickets.length;
  const selectedCount = isGroup ? groupIds.filter((id) => selectedIds.has(id)).length : totalTickets;
  // Tickets under the account they belong to, biggest account first — the same
  // shape EventTransferModal gives the DICE/TAO flow. A group can span several
  // of the seller's accounts and the transfer empties all of them, so the
  // account is the heading rather than a column.
  const accountGroups = Array.from(
    groupTickets.reduce((acc, t) => {
      const label = accountLabel(t);
      const existing = acc.get(label);
      if (existing) existing.push(t);
      else acc.set(label, [t]);
      return acc;
    }, new Map<string, Ticket[]>()),
  )
    .map(([label, tickets]) => ({ label, tickets }))
    .sort((a, b) => b.tickets.length - a.tickets.length);

  // One manual-transfer call carries one account: the endpoint takes
  // {platform, event_id, ticket_ids} with no connection id, so the backend
  // resolves the account from the tickets and signs into exactly one. The batch
  // endpoint splits per connection for the same reason — this one can't, so the
  // selection is pinned to whichever account is picked first.
  const activeAccount =
    accountGroups.find((g) => g.tickets.some((t) => selectedIds.has(t.external_ticket_id)))?.label ??
    null;

  const isAccountLocked = (label: string) => activeAccount !== null && activeAccount !== label;

  const toggleAccountGroup = (label: string) => {
    if (isAccountLocked(label)) return;
    const ids = accountGroups.find((g) => g.label === label)?.tickets.map((t) => t.external_ticket_id) ?? [];
    const allInGroup = ids.every((id) => selectedIds.has(id));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => {
        if (allInGroup) next.delete(id);
        else next.add(id);
      });
      return next;
    });
  };

  const handleSubmit = async (data: TransferFormData) => {
    // Manual validation based on platform
    let valid = true;

    if (isDice) {
      if (transferType === 'email') {
        if (!data.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
          form.setError('email', { message: t('validation.emailInvalid') });
          valid = false;
        }
      } else {
        if (!data.phone) {
          form.setError('phone', { message: 'Phone number is required' });
          valid = false;
        }
      }
    } else if (isTao) {
      if (!data.phone) {
        form.setError('phone', { message: 'Phone number is required' });
        valid = false;
      }
    } else if (isAxs) {
      if (!data.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
        form.setError('email', { message: t('validation.emailInvalid') });
        valid = false;
      }
      if (!data.firstName) {
        form.setError('firstName', { message: 'First name is required' });
        valid = false;
      }
      if (!data.lastName) {
        form.setError('lastName', { message: 'Last name is required' });
        valid = false;
      }
    } else {
      if (!data.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
        form.setError('email', { message: t('validation.emailInvalid') });
        valid = false;
      }
    }

    if (!valid) return;

    try {
      let eventId = '';
      if (typeof ticket.event === 'number') {
        eventId = String(ticket.event);
      } else if (ticket.event && typeof ticket.event === 'object') {
        eventId = String(ticket.event.id);
      }

      if (!eventId) {
        form.setError('root', { message: 'Event ID not found for this ticket' });
        return;
      }

      const ticketIds = isGroup ? groupIds.filter((id) => selectedIds.has(id)) : groupIds;

      if (ticketIds.length === 0) {
        form.setError('root', { message: 'Select at least one ticket to transfer' });
        return;
      }

      // The endpoint signs into one account. The picker already prevents this,
      // so reaching it means the selection got out of step with the lock —
      // better a refusal than a half-transferred group.
      const accountsInSelection = new Set(
        groupTickets.filter((t) => ticketIds.includes(t.external_ticket_id)).map(accountLabel),
      );
      if (accountsInSelection.size > 1) {
        form.setError('root', {
          message: 'One transfer can only send tickets from a single account.',
        });
        return;
      }

      const params: TransferParams = {
        platform,
        eventId,
        ticketIds,
        recipientEmail: usePhone ? undefined : (data.email || undefined),
        recipientPhone: usePhone ? (data.phone || undefined) : undefined,
        recipientFirstName: isAxs ? (data.firstName || undefined) : undefined,
        recipientLastName: isAxs ? (data.lastName || undefined) : undefined,
      };

      const result = await onTransfer(params);

      // AXS wants a code before it will move anything. Hold the form state and
      // show the prompt — closing here would report a transfer that hasn't run.
      if (result.otp_required) {
        setOtp({
          params,
          session: result.otp_session,
          phoneNumber: result.phone_number,
          spent: false,
        });
        return;
      }

      finishTransfer(ticketIds.length);
    } catch (err: any) {
      form.setError('root', { message: err?.message || t('modals.transferTicket.failedToTransfer') });
    }
  };

  const finishTransfer = (ticketCount: number) => {
    trackEvent('ticket_transfer_submitted', { platform, transferType, ticketCount });
    form.reset();
    setOtp(null);
    onClose();
  };

  const handleOtpSubmit = async (code: string) => {
    if (!otp || otpSubmitting) return;
    setOtpSubmitting(true);
    try {
      // Call 2: every field from call 1, unchanged, plus the session and code.
      const result = await onTransfer({ ...otp.params, otpSession: otp.session, otpCode: code });
      // Belt and braces: a second challenge here would mean a fresh session.
      if (result.otp_required) {
        setOtp({
          params: otp.params,
          session: result.otp_session,
          phoneNumber: result.phone_number ?? otp.phoneNumber,
          spent: false,
        });
        return;
      }
      finishTransfer(otp.params.ticketIds.length);
    } catch (err: any) {
      if (err instanceof ManualTransferOtpError && err.code === 'otp_session_invalid') {
        // The session is gone, so there is nothing to type into. Back to the
        // form, with the selection and recipient still filled in.
        setOtp(null);
        form.setError('root', { message: 'That code expired. Start the transfer again.' });
        return;
      }
      if (err instanceof ManualTransferOtpError) {
        // Wrong code. The session went with it — AXS spends it either way — so
        // the only way forward is a new code, never a second try at this one.
        setOtp({ ...otp, spent: true, error: 'That code wasn\u2019t right. Send a new code to try again.' });
        return;
      }
      setOtp({ ...otp, error: err?.message || t('modals.transferTicket.failedToTransfer') });
    } finally {
      setOtpSubmitting(false);
    }
  };

  // There is no resend endpoint: a new code is call 1 again, and the session it
  // returns replaces the old one.
  const handleOtpResend = async () => {
    if (!otp || otpResending || otpSubmitting) return;
    setOtpResending(true);
    try {
      const result = await onTransfer(otp.params);
      if (result.otp_required) {
        setOtp({
          params: otp.params,
          session: result.otp_session,
          phoneNumber: result.phone_number ?? otp.phoneNumber,
          spent: false,
        });
        return;
      }
      // AXS stopped asking — the transfer went through on its own.
      finishTransfer(otp.params.ticketIds.length);
    } catch (err: any) {
      setOtp({ ...otp, error: err?.message || 'Could not send a new code. Try again.' });
    } finally {
      setOtpResending(false);
    }
  };

  // The code step replaces the form rather than sitting under it: the transfer
  // is already in flight on the AXS side, and nothing on the form can change
  // now without starting over. No backdrop dismissal either — a stray click
  // would burn the code the seller just read off their phone.
  if (otp) {
    return (
      <Flex
        align="center"
        justify="center"
        fixed
        inset
        bg="black/80"
        backdrop
        zIndex={1000}
        animate="fadeIn"
      >
        <Card
          fullWidth
          maxWidth="md"
          shadow
          animate="slideUp"
          bg="dark"
          padding="lg"
          hover={false}
          onClick={(e) => e.stopPropagation()}
        >
          <AxsTransferOtpPrompt
            phoneNumber={otp.phoneNumber}
            submitting={otpSubmitting}
            resending={otpResending}
            spent={otp.spent}
            error={otp.error}
            onSubmit={handleOtpSubmit}
            onResend={handleOtpResend}
            onCancel={() => {
              setOtp(null);
              onClose();
            }}
          />
        </Card>
      </Flex>
    );
  }

  return (
    <Flex
      align="center"
      justify="center"
      fixed
      inset
      bg="black/80"
      backdrop
      zIndex={1000}
      animate="fadeIn"
      onClick={onClose}
    >
      <Card
        fullWidth
        maxWidth="md"
        shadow
        animate="slideUp"
        bg="dark"
        padding="lg"
        hover={false}
        onClick={(e) => e.stopPropagation()}
      >
        <Text variant="heading" mb={2}>{t('modals.transferTicket.title')}</Text>

        {ticket.event_name && ticket.venue_name && (
          <Text variant="muted" mb={2}>
            {ticket.event_name} | {ticket.venue_name}
          </Text>
        )}

        {isGroup && (
          <Text variant="muted" mb={2}>
            Transferring {selectedCount} of {totalTickets} tickets to one recipient.
          </Text>
        )}

        {/* Platform badge */}
        <div className="mb-3 inline-flex items-center gap-2">
          <span className="px-2 py-1 text-xs rounded-md bg-blue-500/10 text-blue-400 border border-blue-500/20">
            {platform}
          </span>
        </div>

        {/* Ticket picker. Only for a group: with one ticket there is nothing to
            choose, and the single-ticket flow stays exactly as it was. */}
        {isGroup && (
          <>
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm text-white/60">
                {selectedCount > 0 ? (
                  <><span className="text-white font-medium">{selectedCount}</span> selected</>
                ) : (
                  <span className="text-white/40">Select tickets to transfer</span>
                )}
              </span>
              {accountGroups.length > 1 ? (
                // No cross-account "Select all" to offer: one transfer is one
                // account. Each account heading selects its own.
                selectedCount > 0 && (
                  <button
                    type="button"
                    onClick={() => setSelectedIds(new Set())}
                    className="text-xs text-white/50 hover:text-white transition-colors"
                  >
                    Deselect all
                  </button>
                )
              ) : (
                <button
                  type="button"
                  onClick={toggleAll}
                  className="text-xs text-white/50 hover:text-white transition-colors"
                >
                  {allSelected ? 'Deselect all' : 'Select all'}
                </button>
              )}
            </div>

            <div className="mb-4 space-y-3 max-h-[35vh] overflow-y-auto pr-1">
              {accountGroups.map((group) => {
                const selectedInGroup = group.tickets.filter((t) =>
                  selectedIds.has(t.external_ticket_id),
                ).length;
                const allInGroupSelected = selectedInGroup === group.tickets.length;
                const locked = isAccountLocked(group.label);
                const lockedReason = `One transfer moves tickets from a single account. Deselect ${activeAccount} first.`;
                return (
                  <div
                    key={group.label}
                    className={`rounded-lg bg-white/[0.02] border border-white/[0.06] overflow-hidden ${
                      locked ? 'opacity-40' : ''
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => toggleAccountGroup(group.label)}
                      disabled={locked}
                      title={locked ? lockedReason : undefined}
                      className="w-full flex items-center gap-2 px-3 py-2 bg-white/[0.03] hover:bg-white/[0.05] transition-colors text-left disabled:cursor-not-allowed disabled:hover:bg-white/[0.03]"
                    >
                      <span
                        className={`w-4 h-4 flex-shrink-0 rounded border flex items-center justify-center ${
                          allInGroupSelected
                            ? 'bg-blue-500 border-blue-500'
                            : selectedInGroup > 0
                              ? 'bg-blue-500/30 border-blue-500/60'
                              : 'border-white/20'
                        }`}
                      >
                        {allInGroupSelected && (
                          <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                        {!allInGroupSelected && selectedInGroup > 0 && (
                          <span className="w-2 h-0.5 bg-white rounded-full" />
                        )}
                      </span>
                      <span className="text-sm text-white font-medium truncate">{group.label}</span>
                      <span className="text-xs text-white/40 flex-shrink-0 tabular-nums">
                        {selectedInGroup}/{group.tickets.length}
                      </span>
                    </button>
                    <div className="divide-y divide-white/[0.04]">
                      {group.tickets.map((t, i) => {
                        const checked = selectedIds.has(t.external_ticket_id);
                        return (
                          <label
                            key={t.external_ticket_id}
                            title={locked ? lockedReason : undefined}
                            className={`flex items-center gap-3 px-3 py-2 transition-colors ${
                              locked
                                ? 'cursor-not-allowed'
                                : checked
                                  ? 'cursor-pointer bg-blue-500/[0.06]'
                                  : 'cursor-pointer hover:bg-white/[0.02]'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={locked}
                              onChange={() => toggleTicket(t.external_ticket_id)}
                              className="w-4 h-4 rounded border-white/20 bg-white/5 text-blue-500 focus:ring-blue-500/50 focus:ring-offset-0 disabled:cursor-not-allowed"
                            />
                            <span className="text-xs text-white/40 tabular-nums w-6">#{i + 1}</span>
                            <span className="text-sm text-white/80 truncate">{ticketTypeLabel(t)}</span>
                            <span className="text-xs text-white/30 ml-auto font-mono flex-shrink-0">
                              {ticketShortId(t)}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Only before a pick, to explain why nothing starts checked. Once
                an account is chosen the greyed-out blocks say the rest. */}
            {accountGroups.length > 1 && !activeAccount && (
              <Text variant="muted" size="sm" mb={3}>
                Tickets from one account per transfer. Pick an account to start.
              </Text>
            )}
          </>
        )}

        <form onSubmit={form.handleSubmit(handleSubmit)}>
          {/* DICE: Show transfer type toggle */}
          {isDice && (
            <div className="mb-4 p-3 rounded-lg bg-white/[0.02] border border-white/[0.06]">
              <p className="text-sm text-white/60 mb-2">Transfer by:</p>
              <Flex gap={2}>
                <Button
                  type="button"
                  variant={transferType === 'email' ? 'primary' : 'secondary'}
                  onClick={() => setTransferType('email')}
                >
                  Email
                </Button>
                <Button
                  type="button"
                  variant={transferType === 'phone' ? 'primary' : 'secondary'}
                  onClick={() => setTransferType('phone')}
                >
                  Phone Number
                </Button>
              </Flex>
            </div>
          )}

          {/* Email field — hidden whenever the transfer is addressed by phone */}
          {!usePhone && (
            <Flex direction="col" mb={3}>
              <Input
                {...form.register('email')}
                type="email"
                label="Recipient Email"
                placeholder="recipient@example.com"
                error={!!form.formState.errors.email}
                errorMessage={form.formState.errors.email?.message}
              />
              <Text variant="muted" mt={1}>
                {isAxs
                  ? 'Transfer will only work by email for AXS'
                  : isDice
                    ? 'Recipient must have a connected DICE account with this email'
                    : 'Transfer will be sent to this email address'}
              </Text>
            </Flex>
          )}

          {/* Phone field — DICE phone mode and all TAO transfers */}
          {usePhone && (
            <Flex direction="col" mb={3}>
              <Input
                {...form.register('phone')}
                type="tel"
                label="Recipient Phone Number"
                placeholder="+1 (555) 123-4567"
                error={!!form.formState.errors.phone}
                errorMessage={form.formState.errors.phone?.message}
              />
              <Text variant="muted" mt={1}>
                Must add country code (e.g. +1) before the phone number
              </Text>
            </Flex>
          )}

          {/* AXS: first + last name */}
          {isAxs && (
            <Flex gap={3} mb={3}>
              <Flex direction="col" grow>
                <Input
                  {...form.register('firstName')}
                  type="text"
                  label="First Name"
                  placeholder="John"
                  error={!!form.formState.errors.firstName}
                  errorMessage={form.formState.errors.firstName?.message}
                />
              </Flex>
              <Flex direction="col" grow>
                <Input
                  {...form.register('lastName')}
                  type="text"
                  label="Last Name"
                  placeholder="Doe"
                  error={!!form.formState.errors.lastName}
                  errorMessage={form.formState.errors.lastName?.message}
                />
              </Flex>
            </Flex>
          )}

          {form.formState.errors.root && (
            <Text variant="error" mb={2}>
              {form.formState.errors.root.message}
            </Text>
          )}

          <Flex justify="end" gap={2}>
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={form.formState.isSubmitting}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={form.formState.isSubmitting || selectedCount === 0}
            >
              {form.formState.isSubmitting
                ? 'Transferring...'
                : isGroup
                  ? `Transfer ${selectedCount} ticket${selectedCount === 1 ? '' : 's'}`
                  : 'Transfer Ticket'}
            </Button>
          </Flex>
        </form>
      </Card>
    </Flex>
  );
};
